import { getConfig, isTest } from 'pkg/config/app';

import DateTime from 'pkg/datetime';
import * as logger from 'pkg/logger';
import { delay } from 'pkg/timings';
import { UrlParser } from 'pkg/url';
import uuid from 'pkg/uuid';

const urlParser = new UrlParser();
let token = '';
let easyAuthToken = '';
let lastWriteTimestamp = 0;
const READ_PRIMARY_WINDOW_MS = 3000;

interface Headers {
	[key: string]: string;
}

const defaultRequestHeaders: Headers = {
	'Content-Type': 'application/json',
	'X-Correlation-Id': uuid(),
};

export function setToken(t: string) {
	token = t;
}

export function setEasyAuthToken(t: string) {
	easyAuthToken = t;
}

export function hasToken(): boolean {
	return token?.length > 0;
}

export function setDefaultRequestHeader(h: string, val: string) {
	if (!val) {
		delete defaultRequestHeaders[h];
		return;
	}
	defaultRequestHeaders[h] = val;
}

// With the dev EasyAuth proxy active, the Entra bearer takes Authorization (for
// the gate) and the account token moves to X-TS-Authorization (read by the API).
function buildAuthHeaders(): Headers {
	const headers: Headers = {};

	if (EASYAUTH_DEV_BUILD && easyAuthToken.length > 0) {
		headers['Authorization'] = `Bearer ${easyAuthToken}`;

		if (token.length > 0) {
			headers['X-TS-Authorization'] = `Bearer ${token}`;
		}
	} else if (token.length > 0) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	return headers;
}

export async function request(
	requestUrl = '',
	method = 'GET',
	payload = {},
	additionalHeaders = {},
	retryable = false,
	// `keepalive` lets a small write outlive the page, as one sent while it
	// unloads must.
	init: Pick<RequestInit, 'keepalive'> = {}
) {
	const requestOptions: RequestInit = {
		credentials: 'include',
		mode: 'cors',
		method,
		...init,
	};

	const authorizationHeaders = buildAuthHeaders();

	const requestHeaders = {
		...defaultRequestHeaders,
		...authorizationHeaders,
		...additionalHeaders,
		'X-Client-Version': getConfig().version,
		'X-Time-Zone': DateTime.getTimeZone(),
	};

	requestOptions.headers = requestHeaders;

	if (method !== 'GET' && method !== 'HEAD') {
		if (payload instanceof FormData) {
			requestOptions.body = payload;
		} else if (requestOptions.headers['Content-Type'] === 'application/json') {
			requestOptions.body = JSON.stringify(payload);
		} else {
			const params = new URLSearchParams();
			Object.entries(payload).forEach(([key, val]) => {
				params.append(key, `${val}`);
			});
			requestOptions.body = params;
		}
	}

	// Explicitly delete content-type header for multipart/form-data (set
	// explicitly or implied by a FormData body), allowing the browser to take
	// control over the content-type and boundary
	if (
		payload instanceof FormData ||
		requestOptions.headers['Content-Type'] === 'multipart/form-data'
	) {
		delete requestOptions.headers['Content-Type'];
	}

	const parts = [getConfig().apiUrl];

	if (!requestUrl.startsWith('/v1/')) {
		parts.push('v1');
	}

	parts.push(requestUrl);

	requestUrl = parts.join('/').replace(/([^:])(\/\/+)/g, '$1/');

	let retries = 0;
	let easyAuthRetried = false;
	const maxRetries = isTest() ? 0 : 10;
	const makeRequest = async () => {
		let request;
		try {
			request = await fetch(requestUrl, requestOptions);
		} catch (e) {
			if (retryable) {
				if (retries < maxRetries) {
					retries++;
					logger.debug(`retrying request to`, {
						url: requestUrl,
						delay: 50 * retries,
					});

					await delay(50 * retries);
					return await makeRequest();
				}

				throw new Error('failed after max retries', { cause: e });
			}

			throw e;
		}

		// The local proxy tags EasyAuth's login challenge with X-EasyAuth-Login
		// (vs a genuine API 401, which falls through to the app's own auth
		// handling). Refresh the token silently and retry in place; only a fully
		// expired Entra session forces an interactive redirect (which loses state).
		if (
			EASYAUTH_DEV_BUILD &&
			request.status === 401 &&
			!easyAuthRetried &&
			request.headers.get('X-EasyAuth-Login')
		) {
			const easyAuthDev = await import('pkg/auth/easyauth_dev');

			if (easyAuthDev.isEasyAuthDevEnabled()) {
				easyAuthRetried = true;

				if (await easyAuthDev.reauthSilent()) {
					requestOptions.headers = {
						...(requestOptions.headers as Headers),
						...buildAuthHeaders(),
					};

					return await makeRequest();
				}

				await easyAuthDev.login();
			}
		}

		return request;
	};

	return await makeRequest();
}

export async function get(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	// To compensate for database replication latency, make sure that a read within 3s
	// from the last write (POST/PUT/DELETE/PATCH) are sent to the primary database node
	// lastWriteTimestamp is set by the write operations when they finish
	if (Date.now() - lastWriteTimestamp < READ_PRIMARY_WINDOW_MS) {
		additionalHeaders = { ...additionalHeaders, 'X-Read-Primary': 'true' };
	}

	return await request(
		targetUrl,
		'GET',
		requestPayload,
		additionalHeaders,
		true
	);
}

export async function post(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'POST',
		requestPayload,
		additionalHeaders,
		false
	);
	lastWriteTimestamp = Date.now();
	return result;
}

export async function postWithRetry(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'POST',
		requestPayload,
		additionalHeaders,
		true
	);
	lastWriteTimestamp = Date.now();
	return result;
}

export async function put(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {},
	// `keepalive` for a small write sent while the page unloads.
	init: Pick<RequestInit, 'keepalive'> = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'PUT',
		requestPayload,
		additionalHeaders,
		false,
		init
	);
	lastWriteTimestamp = Date.now();
	return result;
}

export async function putWithRetry(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'PUT',
		requestPayload,
		additionalHeaders,
		true
	);
	lastWriteTimestamp = Date.now();
	return result;
}

export async function patch(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'PATCH',
		requestPayload,
		additionalHeaders,
		false
	);
	lastWriteTimestamp = Date.now();
	return result;
}

export async function destroy(
	uriPattern: string,
	uriParams = {},
	requestPayload = {},
	additionalHeaders = {}
) {
	const targetUrl = urlParser.transform(uriPattern, uriParams);

	const result = await request(
		targetUrl,
		'DELETE',
		requestPayload,
		additionalHeaders,
		false
	);
	lastWriteTimestamp = Date.now();
	return result;
}
